import {
  GameNotificationItem,
  GameNotificationsResponse,
} from "../types/gameNotification"
import { GAME_NOTIFY_FROM } from "../constants/gameNotifications"

function pickString(value: unknown, fallback = ""): string {
  if (typeof value === "string") return value.trim() || fallback
  if (value == null) return fallback
  return String(value).trim() || fallback
}

function pickStringOrNull(value: unknown): string | null {
  const s = pickString(value)
  return s || null
}

function pickNumber(value: unknown, fallback = 0): number {
  if (typeof value === "number" && Number.isFinite(value)) return value
  if (typeof value === "string" && value.trim()) {
    const n = Number(value)
    if (Number.isFinite(n)) return n
  }
  return fallback
}

function pickNumberOrNull(value: unknown): number | null {
  if (value == null || value === "") return null
  const n = pickNumber(value, NaN)
  return Number.isFinite(n) ? n : null
}

function pickBool(value: unknown, fallback = false): boolean {
  if (typeof value === "boolean") return value
  if (value === 1 || value === "1" || value === "true") return true
  if (value === 0 || value === "0" || value === "false") return false
  return fallback
}

function unwrapPayload(data: unknown): Record<string, unknown> {
  if (!data || typeof data !== "object" || Array.isArray(data)) return {}
  const obj = data as Record<string, unknown>
  const nested = obj.data ?? obj.result ?? obj.payload
  if (nested && typeof nested === "object" && !Array.isArray(nested)) {
    return nested as Record<string, unknown>
  }
  return obj
}

export function normalizeGameNotificationItem(
  raw: unknown
): GameNotificationItem | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null
  const obj = raw as Record<string, unknown>
  const notificationId = pickNumber(
    obj.notification_id ?? obj.notificationId ?? obj.id,
    NaN
  )
  if (!Number.isFinite(notificationId)) return null

  return {
    notificationId,
    from: pickString(obj.from, GAME_NOTIFY_FROM),
    title: pickString(obj.title),
    body: pickString(obj.body ?? obj.message ?? obj.text),
    link: pickStringOrNull(obj.link ?? obj.url ?? obj.deepLink ?? obj.deep_link),
    date: pickString(obj.date ?? obj.createdAt ?? obj.created_at ?? obj.createAt),
    isReaded: pickBool(obj.isReaded ?? obj.is_readed ?? obj.isRead ?? obj.read),
    userId: pickNumberOrNull(obj.user_id ?? obj.userId),
  }
}

export function normalizeGameNotificationsResponse(
  data: unknown
): GameNotificationsResponse {
  const payload = unwrapPayload(data)
  const rawItems = payload.items ?? payload.notifications ?? payload.list
  const items: GameNotificationItem[] = []
  if (Array.isArray(rawItems)) {
    for (const row of rawItems) {
      const item = normalizeGameNotificationItem(row)
      if (item) items.push(item)
    }
  }

  return {
    from: pickString(payload.from, GAME_NOTIFY_FROM),
    items,
  }
}

export function countUnreadGameNotifications(
  items: ReadonlyArray<{ isReaded: boolean }>
): number {
  return items.filter((n) => !n.isReaded).length
}
